import { Input } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSendlaneCRUDActions } from '@mm/state/actions/SendlaneCRUDActions';
import { selectSendlaneConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './SendlaneIntegrations.module.css';
import { SendlaneSyncSetup } from './SendlaneSyncSetup';

export const SendlaneSettings = () => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const apiKeys = useSelector((store: StoreState) => store.sendlane.apiKeys);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const sendlaneConnections = useSelector(selectSendlaneConnections);
  const sendlaneConnection = sendlaneConnections?.[0];

  const accessToken = apiKeys.find(
    apiKey => apiKey.id === sendlaneConnection?.sendlaneAccessToken?.id,
  );

  const [apiKey, setApiKey] = useState(accessToken?.privateKey);
  const [error, setError] = useState('');

  const disabledApiKey =
    sendlaneConnection && apiKey === accessToken?.privateKey;

  const { getLists, getApiKeys, createApiKey, updateApiKey } =
    useSendlaneCRUDActions();
  const { update, create } = useConnectionCRUDActions();

  const { openNewBillingModal2023 } = useModalActions();
  const notMyToken = accessToken?.readOnly;

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.SENDLANE_UPGRADE,
      onSuccessBilling,
    });
  };

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!apiKey || apiKey.length < 900) {
      setError('Invalid API Key');
      return;
    }

    const createKey = async (privateKey: string) => {
      const { id } = await createApiKey(privateKey);

      if (!sendlaneConnections.length) {
        await create(activeFbPageId, {
          name: 'Sendlane Connection',
          connectionKind: 'sendlane',
          sendlaneParams: { accessTokenId: id },
        });
      } else if (
        (sendlaneConnections || []).find(
          conn => conn.sendlaneAccessToken?.id !== id,
        )
      ) {
        sendlaneConnections.forEach(connection => {
          update(activeFbPageId, {
            id: connection.id,
            sendlaneParams: { accessTokenId: id },
          });
        });
      }
    };

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (accessToken) {
        updateApiKey(accessToken.id, apiKey);
      } else {
        createKey(apiKey);
      }
      setError('');
    }
  };

  const retrieveSendlaneLists = () => {
    if (accessToken?.id && hasNativeIntegrationsFeature) {
      getLists(activeFbPageId, accessToken.id);
    }
  };

  useEffect(() => {
    getApiKeys(activeFbPageId);
    retrieveSendlaneLists();
  }, []);

  useEffect(() => {
    setApiKey(accessToken?.privateKey);
  }, [accessToken]);

  useEffect(() => {
    if (
      accessToken &&
      !sendlaneConnections.find(conn => conn.audienceId === null)
    ) {
      create(activeFbPageId, {
        name: 'Sendlane Connection',
        connectionKind: 'sendlane',
        sendlaneParams: { accessTokenId: accessToken.id },
      });
    }
  }, [accessToken, sendlaneConnections]);

  return (
    <div className={styles.settingsContent}>
      <div className={classNames(styles.box, styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={styles.heading}>API Key</div>
          <div className={styles.apiKeyInput}>
            <Input
              placeholder="Enter your API Key"
              css={{ container: styles.inputContainer, input: styles.input }}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <PrimaryButton
              className={styles.primaryButton}
              onClick={e => connectApiKey(e)}
              disabled={disabledApiKey}
            >
              {sendlaneConnection && disabledApiKey
                ? 'Connected'
                : sendlaneConnection
                ? 'Update'
                : 'Connect'}
            </PrimaryButton>
          </div>
          <div className={styles.error}>{error}</div>
        </div>
      </div>

      {accessToken && (
        <SendlaneSyncSetup
          accessToken={accessToken}
          controlsDisabled={!!notMyToken}
        />
      )}
    </div>
  );
};
